import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthUserContext } from '../views/session/context';
import { toast } from 'react-toastify';
import { signOut } from 'firebase/auth';
import { getAuth } from 'firebase/auth';

const SessionTimer = () => {
    const { authUser } = useContext(AuthUserContext);
    const [showWarning, setShowWarning] = useState(false);
    const [remainingTime, setRemainingTime] = useState(null);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const navigate = useNavigate();
    const auth = getAuth();
    
    // TESTING: Short session timeout settings
    //const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes for testing
    const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes for testing
    const WARNING_TIME = 1 * 60 * 1000; // Show warning 1 minute before expiry
    
    // Function to handle session expiration
    const handleSessionExpired = useCallback(() => {
        console.log('🔴 SESSION EXPIRED: Logging out user at', new Date().toLocaleTimeString());
        toast.error('Your session has expired. Please sign in again.');
        signOut(auth).then(() => {
            navigate('/login');
        });
    }, [auth, navigate]);
    
    // Function to check session expiration based on elapsed time
    const checkSessionExpiration = useCallback(() => {
        if (!authUser || !sessionStartTime) {
            console.log('⚠️ Session check skipped: No auth user or session start time');
            return;
        }
        
        const currentTime = Date.now();
        const elapsedTime = currentTime - sessionStartTime;
        const timeRemaining = SESSION_TIMEOUT - elapsedTime;
        const inactiveTime = currentTime - lastActivity;
        
        // console.log(`⏱️ SESSION CHECK at ${new Date().toLocaleTimeString()}:`);
        // console.log(`   ├─ Session started: ${new Date(sessionStartTime).toLocaleTimeString()} (${Math.floor(elapsedTime/1000)}s ago)`);
        // console.log(`   ├─ Time remaining: ${Math.floor(timeRemaining/1000)}s (${Math.floor(timeRemaining/60000)}m ${Math.floor((timeRemaining % 60000)/1000)}s)`);
        // console.log(`   ├─ Last activity: ${new Date(lastActivity).toLocaleTimeString()} (${Math.floor(inactiveTime/1000)}s ago)`);
        // console.log(`   └─ Warning state: ${showWarning ? 'ACTIVE' : 'inactive'}`);
        
        setRemainingTime(timeRemaining);
        
        // Show warning if session is about to expire
        if (timeRemaining <= WARNING_TIME && timeRemaining > 0) {
            if (!showWarning) {
                console.log('🟠 WARNING: Session about to expire in less than 1 minute');
                setShowWarning(true);
            }
        } else {
            if (showWarning) {
                console.log('🟢 Warning cleared: Session extended or more than 1 minute remaining');
                setShowWarning(false);
            }
        }
        
        // If session has expired, log out
        if (timeRemaining <= 0) {
            console.log('🔴 SESSION TIMEOUT REACHED after 5 minutes of inactivity');
            handleSessionExpired();
        }
    }, [authUser, sessionStartTime, lastActivity, showWarning, handleSessionExpired]);
    
    // Initialize session timer when user logs in
    useEffect(() => {
        if (authUser && !sessionStartTime) {
            const now = Date.now();
            console.log('🟢 INITIALIZING SESSION TIMER at', new Date(now).toLocaleTimeString());
            console.log(`   ├─ Timeout set to: ${SESSION_TIMEOUT/60000} minutes`);
            console.log(`   └─ Warning set to: ${WARNING_TIME/60000} minute before expiry`);
            setSessionStartTime(now);
            setLastActivity(now);
        }
    }, [authUser, sessionStartTime]);
    
    // Set up activity monitoring and session checks
    useEffect(() => {
        if (!authUser) return;
        
       // console.log('🟢 SETTING UP SESSION MONITORING');
        
        // Check session expiration immediately
        checkSessionExpiration();
        
        // Set up interval to check session expiration
        const intervalId = setInterval(() => {
            //console.log('\n📋 SCHEDULED 10-SECOND CHECK at', new Date().toLocaleTimeString());
            checkSessionExpiration();
        }, 10000); // Check every 10 seconds for more visible testing
        
        // Set up event listeners for user activity
        const resetTimer = (event) => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivity;
            
            // Only log if it's been more than 2 seconds since the last activity
            // to avoid console spam from continuous events like mousemove
            if (timeSinceLastActivity > 2000) {
                // console.log(`\n🟢 USER ACTIVITY DETECTED (${event.type}) at ${new Date(now).toLocaleTimeString()}`);
                // console.log(`   └─ Previous activity: ${Math.floor(timeSinceLastActivity/1000)}s ago`);
                
                // Reset the session start time on user activity
                setSessionStartTime(now);
                setLastActivity(now);
                
                if (showWarning) {
                    console.log('   └─ Warning cleared due to user activity');
                    setShowWarning(false);
                }
            } else {
                // Still update the last activity time without logging
                setLastActivity(now);
            }
        };
        
        // Monitor user activity
        window.addEventListener('mousemove', resetTimer);
        window.addEventListener('keypress', resetTimer);
        window.addEventListener('click', resetTimer);
        window.addEventListener('scroll', resetTimer);
        
        return () => {
           //console.log('🔵 CLEANING UP SESSION TIMER');
            clearInterval(intervalId);
            window.removeEventListener('mousemove', resetTimer);
            window.removeEventListener('keypress', resetTimer);
            window.removeEventListener('click', resetTimer);
            window.removeEventListener('scroll', resetTimer);
        };
    }, [authUser, checkSessionExpiration, lastActivity, showWarning]);
    
    // Render warning modal if session is about to expire
    if (showWarning && remainingTime) {
        const minutes = Math.floor(remainingTime / 60000);
        const seconds = Math.floor((remainingTime % 60000) / 1000);

    return (
            <div className="session-timeout-warning" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999
            }}>
                <div className="warning-modal" style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '400px',
                    width: '100%',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                }}>
                    <h3 style={{ color: '#dc3545', marginBottom: '15px' }}>Session Expiring Soon</h3>
                    <p style={{ marginBottom: '20px', fontSize: '16px' }}>
                        Your session will expire in {minutes}:{seconds < 10 ? `0${seconds}` : seconds}.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button 
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                const now = Date.now();
                                console.log('🟢 SESSION EXTENDED MANUALLY at', new Date(now).toLocaleTimeString());
                                // Reset the session timer
                                setSessionStartTime(now);
                                setLastActivity(now);
                                setShowWarning(false);
                                toast.success('Session extended successfully');
                            }}
                        >
                            Stay Logged In
                        </button>
                        <button 
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                console.log('🔵 USER INITIATED LOGOUT at', new Date().toLocaleTimeString());
                                signOut(auth).then(() => {
                                    navigate('/login');
                                    toast.info('You have been logged out');
                                });
                            }}
                        >
                            Logout Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Add a small indicator to show the session timer is active
    return (
        <div style={{ 
            position: 'fixed', 
            bottom: '10px', 
            right: '10px', 
            backgroundColor: 'rgba(0,0,0,0.1)', 
            padding: '5px 10px', 
            borderRadius: '5px',
            fontSize: '12px',
            zIndex: 1000
        }}>
            {remainingTime ? (
                <span>
                    Session: {Math.floor(remainingTime / 60000)}m {Math.floor((remainingTime % 60000) / 1000)}s
                </span>
            ) : (
                <span>Session timer active</span>
            )}
        </div>
    );
};

export default SessionTimer; 